import { getObjectInitialCompletionSuccess } from '../../../../../../redux/reducers/insertion/insertionFeature'
import { getInsertionGeoCoordinatesCompletionSuccess } from '../../../../../../redux/reducers/address/addressFeature'
import { queryGeoCodes } from '../../../../../../redux/thunks/gisService/gisService'
import { getAttachmentsInitialCompletionSuccess } from '../../../../../../redux/reducers/multimedia/multimediaFeature'
import { configureObjectChooser } from '../../../../../../redux/actions/flowInformation/flowInformation'
import { DisplayObjectChooserType } from '../../../../../../@types/object-chooser-types'
import { useCallback, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../../../app/hooks'
import { ConvertedRealEstate } from '../../../../../../@types/real-estate-api-types'
import { FlowMetaData, GeoCoordinates, ObjectParams } from '../../../../../../@types/types'
import { validateCurrentInsertion } from '../../../../../../redux/thunks/insertionData/insertionData'
import { DraftReuseCandidateParams, findDraftReuseCandidate } from '../../../../../../api/object/objectApi'
import { API_ENDPOINT } from '../../../../../../config'
import { preserveSearchParams } from '../../../../../../utils/navigation'
import { useTranslation } from 'react-i18next'
import { useToastContext } from '../../../../../../app/toast/toastContext'
import { InitState } from '../../../../../../redux/reducers/root/initialState'
import { useFeatureSwitch } from '../../../../../../utils/features/FeatureSwitchContext'
import { FeatureSwitch } from '../../../../../../utils/features/featureSwitches'

type DraftReuseModalState = {
  object: ConvertedRealEstate
  request: DraftReuseCandidateParams
  draftIdentifier: string
}

export type ObjectSelectionResult = 'reuse_prompt' | 'create' | 'ignored'

export function useOpenWizardWithCurrentDataCallback(
  object: ConvertedRealEstate,
  createNewRealEstateInOma: boolean = false
) {
  const openWizardWithCurrentData = useOpenWizardWithCurrentData()

  return useCallback(() => {
    openWizardWithCurrentData(object, createNewRealEstateInOma)
  }, [createNewRealEstateInOma, object, openWizardWithCurrentData])
}

export function useOpenWizardWithCurrentData() {
  const dispatch = useAppDispatch()

  return useCallback(
    (object: ConvertedRealEstate, createNewRealEstateInOma: boolean = false) => {
      const { listing, meta, geoCoordinates } = object

      dispatch(
        getObjectInitialCompletionSuccess({
          insertionData: { ...listing, published: false },
          insertionIdentification: {
            ...meta,
            realEstateId: createNewRealEstateInOma ? undefined : meta.realEstateId,
          },
        })
      )
      if (hasValidGeoCoordinates(geoCoordinates)) {
        dispatch(getInsertionGeoCoordinatesCompletionSuccess({ geoCoordinates: geoCoordinates! }))
      } else {
        dispatch(queryGeoCodes())
      }
      object.attachments && dispatch(getAttachmentsInitialCompletionSuccess({ initAttachments: object.attachments }))

      dispatch(configureObjectChooser(DisplayObjectChooserType.NO, undefined, true, true))

      dispatch(validateCurrentInsertion())
    },
    [dispatch]
  )
}

export function useDraftReuseObjectSelection() {
  const { t } = useTranslation()
  const { addToast } = useToastContext()
  const { isActive: isDraftReuseEnabled } = useFeatureSwitch(FeatureSwitch.PPA_12594_OBJECT_CHOOSER_DRAFT_REUSE)
  const flowFeature = useAppSelector((state: InitState) => state.flowFeature)
  const openWizardWithCurrentData = useOpenWizardWithCurrentData()
  const [modalState, setModalState] = useState<DraftReuseModalState | null>(null)
  const [selectedObjectId, setSelectedObjectId] = useState<string | null>(null)
  const [isConfirmingReuse, setIsConfirmingReuse] = useState(false)

  const buildRequest = useCallback(
    (object: ConvertedRealEstate): DraftReuseCandidateParams | null => {
      const buildingId = object.meta.buildingId
      const metaData = resolveFlowMetaData(flowFeature)

      if (!buildingId || !metaData) {
        return null
      }

      const request: DraftReuseCandidateParams = {
        type: metaData.category as ObjectParams['type'],
        transaction: metaData.transaction,
        segment: metaData.segmentation,
        buildingId,
      }

      if (object.meta.unitId) {
        request.unitId = object.meta.unitId
      }

      return request
    },
    [flowFeature]
  )

  const startNewListing = useCallback(
    (object: ConvertedRealEstate) => {
      openWizardWithCurrentData(object, true)
    },
    [openWizardWithCurrentData]
  )

  const redirectToDraft = useCallback((draftIdentifier: string) => {
    window.location.href = preserveSearchParams(
      `${API_ENDPOINT}/anbieten/private-anbieter/inserieren/realEstate/offer-api/${encodeURIComponent(draftIdentifier)}`
    )
  }, [])

  const handleObjectSelection = useCallback(
    async (object: ConvertedRealEstate): Promise<ObjectSelectionResult> => {
      if (selectedObjectId) {
        return 'ignored'
      }

      if (!isDraftReuseEnabled) {
        startNewListing(object)
        return 'create'
      }

      const request = buildRequest(object)
      if (!request) {
        startNewListing(object)
        return 'create'
      }

      setSelectedObjectId(object.meta.id)

      try {
        const response = await findDraftReuseCandidate(request)
        if (response.draftIdentifier) {
          setModalState({ object, request, draftIdentifier: response.draftIdentifier })
          return 'reuse_prompt'
        }
      } catch {
        // Fallback to the existing create flow if draft reuse lookup is temporarily unavailable.
      } finally {
        setSelectedObjectId(null)
      }

      startNewListing(object)
      return 'create'
    },
    [buildRequest, isDraftReuseEnabled, selectedObjectId, startNewListing]
  )

  const closeDraftReuseModal = useCallback(() => {
    if (isConfirmingReuse) {
      return
    }
    setModalState(null)
  }, [isConfirmingReuse])

  const declineDraftReuse = useCallback(() => {
    if (!modalState) {
      return
    }

    const { object } = modalState
    setModalState(null)
    startNewListing(object)
  }, [modalState, startNewListing])

  const confirmDraftReuse = useCallback(async () => {
    if (!modalState) {
      return
    }

    setIsConfirmingReuse(true)

    try {
      const response = await findDraftReuseCandidate(modalState.request)
      if (response.draftIdentifier) {
        redirectToDraft(response.draftIdentifier)
        return
      }
    } catch {
      // The candidate may no longer be editable or available; show a toast below.
    } finally {
      setIsConfirmingReuse(false)
    }

    setModalState(null)
    addToast({
      headline: t('objectChooser.reuseModal.error.headline'),
      message: t('objectChooser.reuseModal.error.message'),
      type: 'error',
      timeout: 6000,
      variant: 'elevated',
    })
  }, [addToast, modalState, redirectToDraft, t])

  return useMemo(
    () => ({
      draftReuseModalState: modalState,
      isCheckingDraftReuseForObjectId: selectedObjectId,
      isConfirmingDraftReuse: isConfirmingReuse,
      handleObjectSelection,
      closeDraftReuseModal,
      declineDraftReuse,
      confirmDraftReuse,
    }),
    [
      closeDraftReuseModal,
      confirmDraftReuse,
      declineDraftReuse,
      handleObjectSelection,
      isConfirmingReuse,
      modalState,
      selectedObjectId,
    ]
  )
}

const hasValidGeoCoordinates = (coordinates: GeoCoordinates | undefined) => {
  return !!(coordinates && !(coordinates.latitude == 0 && coordinates.longitude == 0))
}

const resolveFlowMetaData = (flowFeature: InitState['flowFeature']) => {
  const flowMetaData = flowFeature.flow?.flowInformation.metaData
  if (hasRequiredFlowMetaData(flowMetaData)) {
    return flowMetaData
  }

  const storedMetaData = flowFeature.flowInformation.metaData
  if (hasRequiredFlowMetaData(storedMetaData)) {
    return storedMetaData
  }

  return null
}

const hasRequiredFlowMetaData = (metaData: Partial<FlowMetaData> | undefined): metaData is FlowMetaData =>
  !!metaData?.category && !!metaData?.transaction && !!metaData?.segmentation
